import type { LucideIcon } from "lucide-react";
import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/shared/lib/utils";
import { useFloatingPosition, type Placement } from "./floating";

const MenuCtx = createContext<{ close: () => void } | null>(null);

type DropdownMenuProps = {
  /** Elemento que abre o menu (Button/IconButton). Recebe onClick, aria-haspopup e aria-expanded. */
  trigger: ReactElement;
  children: ReactNode;
  align?: "start" | "end";
  side?: "bottom" | "top";
  /** Rótulo acessível do menu (ex.: "Ações do usuário"). */
  label?: string;
  className?: string;
  /** Largura mínima do painel (CSS). */
  minWidth?: string;
};

/**
 * Menu suspenso acessível (role="menu"): setas/Home/End navegam, Enter/Espaço ativam,
 * Esc fecha e devolve o foco ao gatilho, clique fora fecha.
 *
 * <DropdownMenu label="Ações" trigger={<IconButton icon={MoreHorizontal} label="Mais ações" />}>
 *   <DropdownMenuItem icon={Pencil} onSelect={edit}>Editar</DropdownMenuItem>
 *   <DropdownMenuSeparator />
 *   <DropdownMenuItem icon={Trash2} tone="danger" onSelect={remove}>Excluir</DropdownMenuItem>
 * </DropdownMenu>
 */
export function DropdownMenu({ trigger, children, align = "end", side = "bottom", label, className, minWidth = "11rem" }: DropdownMenuProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const placement = `${side}-${align}` as Placement;
  const pos = useFloatingPosition(open, anchorRef, menuRef, placement, 4);

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) {
      window.setTimeout(() => anchorRef.current?.querySelector<HTMLElement>("button, [tabindex], a")?.focus(), 0);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      close(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, close]);

  // Foco no primeiro item ao abrir.
  useEffect(() => {
    if (!open || !pos) return;
    const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])');
    first?.focus();
  }, [open, pos]);

  const onMenuKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? []);
    const idx = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(idx + 1) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "Tab") {
      close(false);
    }
  };

  const triggerEl = isValidElement(trigger)
    ? cloneElement(trigger as ReactElement<Record<string, unknown>>, {
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-controls": open ? id : undefined,
        onClick: (e: ReactMouseEvent) => {
          e.stopPropagation();
          (trigger.props as { onClick?: (e: ReactMouseEvent) => void }).onClick?.(e);
          setOpen((o) => !o);
        },
        onKeyDown: (e: ReactKeyboardEvent) => {
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
          }
        },
      })
    : trigger;

  return (
    <MenuCtx.Provider value={{ close }}>
      <span ref={anchorRef} className="inline-flex">
        {triggerEl}
      </span>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              id={id}
              role="menu"
              aria-label={label}
              tabIndex={-1}
              onKeyDown={onMenuKey}
              style={{ position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? -9999, minWidth }}
              className={cn(
                "z-[250] max-h-[min(24rem,70vh)] overflow-auto rounded-lg border border-border bg-surface-raised p-1 text-body text-fg shadow-md outline-none",
                pos ? "animate-pop-in" : "opacity-0",
                className,
              )}
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </MenuCtx.Provider>
  );
}

type ItemProps = {
  children: ReactNode;
  icon?: LucideIcon;
  onSelect?: () => void;
  tone?: "default" | "danger";
  disabled?: boolean;
  /** Texto à direita (atalho, contagem…). */
  trailing?: ReactNode;
  /** Mantém o menu aberto após selecionar. */
  keepOpen?: boolean;
};

export function DropdownMenuItem({ children, icon: Icon, onSelect, tone = "default", disabled, trailing, keepOpen }: ItemProps) {
  const ctx = useContext(MenuCtx);
  const activate = () => {
    if (disabled) return;
    onSelect?.();
    if (!keepOpen) ctx?.close();
  };
  return (
    <div
      role="menuitem"
      tabIndex={-1}
      aria-disabled={disabled || undefined}
      onClick={(e) => {
        e.stopPropagation();
        activate();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      }}
      className={cn(
        "flex h-8 cursor-pointer select-none items-center gap-2 rounded-md px-2 text-body outline-none transition-colors",
        "hover:bg-surface-hover focus:bg-surface-hover",
        tone === "danger" ? "text-danger-fg hover:bg-danger-subtle focus:bg-danger-subtle" : "text-fg",
        disabled && "pointer-events-none text-fg-disabled",
      )}
    >
      {Icon ? <Icon className={cn("h-4 w-4 shrink-0", tone === "danger" ? "" : "text-fg-subtle")} aria-hidden /> : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {trailing ? <span className="text-caption text-fg-subtle">{trailing}</span> : null}
    </div>
  );
}

export function DropdownMenuSeparator() {
  return <div role="separator" className="-mx-1 my-1 h-px bg-border" />;
}

export function DropdownMenuLabel({ children }: { children: ReactNode }) {
  return <div className="px-2 pb-1 pt-1.5 text-overline uppercase text-fg-subtle">{children}</div>;
}
